import { Flatfile } from '@flatfile/api-internal'
import { AppType } from '@flatfile/api-internal/api'
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconName,
  Input,
  Label,
  Separator,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@flatfile/baseline'
import { copyToClipboard } from '@flatfile/shared-ui'
import { snakeCase } from 'moderndash'
import React, { useEffect, useRef, useState } from 'react'
import { renderMappedIcon, validBaselineIcons } from '../../components/Shared/IconMapper'
import { useIdentity } from '../../hooks/useIdentity'
import { useCollection } from '../../resources/hooks/useCollection'
import { useUpdateResource } from '../../resources/hooks/useResourceMutation'
import { logger } from '../../utils/logging'

export const UpdateAppForm = (props: {
  closeModal?: () => void
  currentApp: Flatfile.App
  isBuiltInApp?: boolean
  saveButton?: string
  onSubmit?: () => void
  disableNamespace?: boolean
}) => {
  const { currentApp } = props
  const { toast } = useToast()

  const { identity } = useIdentity()
  const apps = useCollection('APPS')
  const appIdInputRef = useRef<HTMLInputElement>(null)
  const namespaceInputRef = useRef<HTMLInputElement>(null)

  const [formValues, setFormValues] = useState({
    name: currentApp?.name || '',
    namespace: currentApp?.namespace || '',
    entity: currentApp?.entity || '',
    entityPlural: currentApp?.entityPlural || '',
    icon: currentApp?.icon || '',
  })

  const [formErrors, setFormErrors] = useState<Record<string, string | null>>({
    name: null,
    namespace: null,
    entity: null,
    entityPlural: null,
    icon: null,
  })

  const [defaultAppChecked, setDefaultAppChecked] = useState(false)
  const isCurrentDefaultApp = identity.account.defaultAppId === currentApp?.id

  const [prodChecked, setProdChecked] = useState(currentApp?.environmentFilters?.type?.includes('PROD') ?? false)

  useEffect(() => {
    setFormValues({
      name: currentApp?.name || '',
      namespace: currentApp?.namespace || '',
      entity: currentApp?.entity || '',
      entityPlural: currentApp?.entityPlural || '',
      icon: currentApp?.icon || '',
    })
  }, [currentApp])

  // Validate a field and return an error message or null
  const validateField = (field: string, value: string): string | null => {
    switch (field) {
      case 'name': {
        return value.trim().length > 0 ? null : 'Enter a valid string'
      }
      case 'namespace': {
        if (value === currentApp?.namespace) {
          return null
        }

        const namespaces = apps.map((app: Flatfile.App) => app.namespace)
        const unavailableNamespaces = ['portal', 'projects', 'workbooks', 'mapping', ...namespaces]

        const isUpdatingExistingApp = currentApp && currentApp.namespace
        if (isUpdatingExistingApp) {
          const index = unavailableNamespaces.indexOf(currentApp.namespace)
          if (index > -1) {
            unavailableNamespaces.splice(index, 1)
          }
        }

        if (unavailableNamespaces.includes(value)) {
          return 'This namespace is reserved or already in use.'
        }
        return value.trim().length > 0 ? null : 'Set a namespace for your app.'
      }
      case 'entity': {
        return value.trim().length > 0 ? null : 'Enter a valid string.'
      }
      case 'entityPlural': {
        return value.trim().length > 0 ? null : 'Enter a valid string.'
      }
      case 'icon': {
        // No validation needed for icon now that we use a dropdown with predefined values
        return null
      }
      default:
        return null
    }
  }

  // Handle input changes
  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFormValues((prev) => ({ ...prev, [field]: value }))
    setFormErrors((prev) => ({ ...prev, [field]: validateField(field, value) }))
  }

  // Auto-generate namespace from name
  useEffect(() => {
    if (!props.disableNamespace && !props.isBuiltInApp) {
      const name = formValues.name
      const generatedNamespace = snakeCase(name)
      setFormValues((prev) => ({ ...prev, namespace: generatedNamespace }))
      setFormErrors((prev) => ({ ...prev, namespace: validateField('namespace', generatedNamespace) }))
    }
  }, [formValues.name, props.disableNamespace, props.isBuiltInApp])

  const [updateApp] = useUpdateResource('APPS')
  const [updateAccount] = useUpdateResource('ACCOUNTS')

  const validateForm = (): boolean => {
    const newErrors = {
      name: validateField('name', formValues.name),
      namespace: validateField('namespace', formValues.namespace),
      entity: validateField('entity', formValues.entity),
      entityPlural: validateField('entityPlural', formValues.entityPlural),
      icon: validateField('icon', formValues.icon),
    }

    setFormErrors(newErrors)
    return !Object.values(newErrors).some((error) => error !== null)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateForm()) return

    const environmentTypes = ['DEV']
    if (prodChecked) environmentTypes.push('PROD')

    const requestData = {
      ...formValues,
      environmentFilters: {
        type: environmentTypes,
      },
    }

    try {
      const updatedApp = await updateApp(currentApp.id, requestData)
      if (!updatedApp) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to update App.',
        })
        return
      }

      if (defaultAppChecked) {
        updateAccount({ defaultAppId: updatedApp.id })
      }

      props.onSubmit?.()
      props.closeModal?.()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update App.',
      })
      logger.error('Failed to update App', { error })
    }
  }

  const isBuiltInApp =
    currentApp?.namespace === 'portal' ||
    currentApp?.namespace === 'projects' ||
    currentApp?.namespace === 'workbooks' ||
    currentApp?.namespace === 'mapping'

  // Custom copy function to use with input refs
  const handleCopy = (ref: React.RefObject<HTMLInputElement>) => {
    if (ref.current) {
      copyToClipboard(ref.current.value)
    }
  }

  return (
    <div className='w-full'>
      {isCurrentDefaultApp && (
        <Alert className='mb-4'>
          <div className='flex items-center gap-2'>
            <Icon name='circle-info' className='h-5 w-5' />
            <AlertDescription>
              <Text variant='callout'>
                <span className='font-bold'>{currentApp?.name}</span> serves as your default landing page and is the
                central hub where all unassigned Spaces are gathered. Modify this setting by visiting another App and
                selecting it as the default App for the Account.
              </Text>
            </AlertDescription>
          </div>
        </Alert>
      )}

      {isBuiltInApp && (
        <Alert variant='destructive' className='mb-4'>
          <div className='flex items-center gap-2'>
            <Icon name='triangle-exclamation' className='h-5 w-5' />
            <AlertDescription>
              <Text variant='callout'>
                <span className='font-bold'>Trying to make changes?</span> Some of the details of{' '}
                <span className='font-bold'>{currentApp?.name}</span> cannot be modified because it is a built-in App.
                Create a custom App to control these details.
              </Text>
            </AlertDescription>
          </div>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <Label htmlFor='appId'>App ID</Label>
          <div className='flex items-center mt-1'>
            <Input id='appId' defaultValue={currentApp?.id} disabled className='flex-1' ref={appIdInputRef} />
            <Button variant='ghost' size='icon-md' onClick={() => handleCopy(appIdInputRef)} icon='copy-squares' />
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Name Input */}
          <div>
            <Label htmlFor='name'>Name</Label>
            <Input
              id='name'
              value={formValues.name}
              onChange={handleInputChange('name')}
              placeholder='example: My Custom App'
              disabled={isBuiltInApp}
              hasError={!!formErrors.name}
              className='mt-1'
            />
            {formErrors.name && <p className='text-xs text-destructive mt-1'>{formErrors.name}</p>}
          </div>

          {/* Namespace Input */}
          <div>
            <div className='flex items-center gap-1'>
              <Label htmlFor='namespace'>Namespace</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex items-center ml-1 cursor-pointer'>
                    <Icon name='circle-info' className='h-4 w-4 text-text-default hover:text-text-title' />
                  </span>
                </TooltipTrigger>
                <TooltipContent side='top' className='flex items-center px-2 py-1 max-w-[300px] z-[60]'>
                  <Text variant='body' className='text-sm'>
                    Namespace is auto-generated from the name of the App. This may be changed, however it may also
                    create unexpected results if changed. Please make note of this value before changing it for ease of
                    reversion in the case that you must change it back.
                  </Text>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className='flex items-center mt-1'>
              <Input
                id='namespace'
                value={formValues.namespace}
                onChange={handleInputChange('namespace')}
                placeholder='example: my-custom-app'
                disabled={isBuiltInApp || props.disableNamespace}
                hasError={!!formErrors.namespace}
                className='flex-1'
                ref={namespaceInputRef}
              />
              <Button
                variant='ghost'
                size='icon-md'
                onClick={() => handleCopy(namespaceInputRef)}
                disabled={isBuiltInApp || props.disableNamespace}
                icon='copy-squares'
              />
            </div>
            {formErrors.namespace && <p className='text-xs text-destructive mt-1'>{formErrors.namespace}</p>}
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Entity Input */}
          <div>
            <div className='flex items-center gap-1'>
              <Label htmlFor='entity'>Entity</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex items-center ml-1 cursor-pointer'>
                    <Icon name='circle-info' className='h-4 w-4 text-text-default hover:text-text-title' />
                  </span>
                </TooltipTrigger>
                <TooltipContent side='top' className='flex items-center px-2 py-1 max-w-[300px] z-[60]'>
                  <Text variant='body' className='text-sm'>
                    This will give all the Spaces you make an easy-to-understand entity name.
                  </Text>
                </TooltipContent>
              </Tooltip>
            </div>
            <Input
              id='entity'
              value={formValues.entity}
              onChange={handleInputChange('entity')}
              placeholder='example: session'
              disabled={isBuiltInApp}
              hasError={!!formErrors.entity}
              className='mt-1'
            />
            {formErrors.entity && <p className='text-xs text-destructive mt-1'>{formErrors.entity}</p>}
          </div>

          {/* Entity Plural Input */}
          <div>
            <div className='flex items-center gap-1'>
              <Label htmlFor='entityPlural'>Entity Plural</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex items-center ml-1 cursor-pointer'>
                    <Icon name='circle-info' className='h-4 w-4 text-text-default hover:text-text-title' />
                  </span>
                </TooltipTrigger>
                <TooltipContent side='top' className='flex items-center px-2 py-1 max-w-[300px] z-[60]'>
                  <Text variant='body' className='text-sm'>
                    This should simply be the plural version of the Entity name.
                  </Text>
                </TooltipContent>
              </Tooltip>
            </div>
            <Input
              id='entityPlural'
              value={formValues.entityPlural}
              onChange={handleInputChange('entityPlural')}
              placeholder='example: sessions'
              disabled={isBuiltInApp}
              hasError={!!formErrors.entityPlural}
              className='mt-1'
            />
            {formErrors.entityPlural && <p className='text-xs text-destructive mt-1'>{formErrors.entityPlural}</p>}
          </div>
        </div>

        {/* Icon selection dropdown */}
        <div>
          <Label htmlFor='icon'>Custom Icon</Label>
          <div className='mt-1'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' className='w-full flex justify-between items-center' disabled={isBuiltInApp}>
                  <div className='flex items-center gap-2'>
                    {formValues.icon ? (
                      <>
                        {renderMappedIcon({ iconName: formValues.icon as IconName, size: 16 })}
                        <span>{formValues.icon}</span>
                      </>
                    ) : (
                      <span className='text-text-default'>Select an icon</span>
                    )}
                  </div>
                  <Icon name='chevron-down-small' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='max-h-[300px] overflow-y-auto w-[300px]'>
                <div className='p-2'>
                  <Input
                    placeholder='Search icons...'
                    className='mb-2'
                    onChange={(e) => {
                      const searchElement = document.getElementById('icon-search-results')
                      if (searchElement) {
                        const term = e.target.value.toLowerCase()
                        const items = searchElement.querySelectorAll('[data-icon-name]')
                        items.forEach((item) => {
                          const iconName = item.getAttribute('data-icon-name')?.toLowerCase() || ''
                          if (iconName.includes(term)) {
                            item.classList.remove('hidden')
                          } else {
                            item.classList.add('hidden')
                          }
                        })
                      }
                    }}
                  />
                </div>
                <div id='icon-search-results' className='flex flex-col p-2 gap-1'>
                  {Array.from(validBaselineIcons).map((iconName) => (
                    <DropdownMenuItem
                      key={iconName}
                      data-icon-name={iconName}
                      className='flex items-center py-2 px-3'
                      onClick={() => {
                        setFormValues((prev) => ({ ...prev, icon: iconName }))
                        setFormErrors((prev) => ({ ...prev, icon: null }))
                      }}
                    >
                      {renderMappedIcon({ iconName, size: 16 })}
                      <span className='ml-3 text-sm'>{iconName}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {formErrors.icon && <p className='text-xs text-destructive mt-1'>{formErrors.icon}</p>}
        </div>

        <div className='space-y-2 mt-4'>
          <Text variant='callout' className='font-medium'>
            App Visibility
          </Text>
          <Separator className='my-2' />

          {apps.length > 0 && currentApp.type !== AppType.Mapping && !isCurrentDefaultApp && (
            <div className='flex items-center gap-2 py-2'>
              <Checkbox
                id='defaultApp'
                checked={defaultAppChecked}
                onCheckedChange={(checked) => setDefaultAppChecked(checked === true)}
              />
              <Label htmlFor='defaultApp'>Set as Default App for Account</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex items-center ml-1 cursor-pointer'>
                    <Icon name='circle-info' className='h-4 w-4 text-text-default hover:text-text-title' />
                  </span>
                </TooltipTrigger>
                <TooltipContent side='top' className='flex items-center px-2 py-1 max-w-[300px] z-[60]'>
                  <Text variant='body' className='text-sm'>
                    Select this option to set the App as your default landing page, and where all orphaned Spaces will
                    be consolidated. You can change this setting anytime.
                  </Text>
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          <div className='flex items-center gap-2 pb-2'>
            <Checkbox
              id='prodEnv'
              checked={prodChecked}
              onCheckedChange={(checked) => setProdChecked(checked === true)}
            />
            <Label htmlFor='prodEnv'>Show in Production Environments</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex items-center ml-1 cursor-pointer'>
                  <Icon name='circle-info' className='h-4 w-4 text-text-default hover:text-text-title' />
                </span>
              </TooltipTrigger>
              <TooltipContent side='top' className='flex items-center px-2 py-1 max-w-[300px] z-[60]'>
                <Text variant='body' className='text-sm'>
                  Ticking this box grants the App access across all Production Environments. You can undo this at any
                  time.
                </Text>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <Button type='submit' className='w-full mt-4'>
          {props.saveButton ? props.saveButton : 'Update App'}
        </Button>
      </form>
    </div>
  )
}

const UpdateAppModal = React.forwardRef((props: { currentApp: Flatfile.App; isBuiltInApp?: boolean }, _ref) => {
  const { currentApp } = props
  const [modalOpen, setModalOpen] = useState(false)

  const isBuiltInApp =
    currentApp?.namespace === 'portal' ||
    currentApp?.namespace === 'projects' ||
    currentApp?.namespace === 'workbooks' ||
    currentApp?.namespace === 'mapping'

  return currentApp?.id ? (
    <>
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <div className='flex justify-between items-center'>
              <DialogTitle>
                {currentApp?.name
                  ? `${currentApp.name.substring(0, 1).toUpperCase()}${
                      currentApp.name.length > 32
                        ? `${currentApp.name.substring(1, 32)}...`
                        : currentApp.name.substring(1)
                    }`
                  : 'App'}
              </DialogTitle>
              <DialogClose>
                <Icon name='cross-large' size={16} />
              </DialogClose>
            </div>
          </DialogHeader>
          <UpdateAppForm {...props} closeModal={() => setModalOpen(false)} disableNamespace />
        </DialogContent>
      </Dialog>

      {isBuiltInApp ? (
        <div className='-mr-[0.5rem]'>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex items-center cursor-pointer' onClick={() => setModalOpen(true)}>
                <Icon name='circle-info' size={16} className='text-text-default hover:text-text-title p-2' />
              </span>
            </TooltipTrigger>
            <TooltipContent className='flex items-center px-2 py-1 z-[60]'>
              <Text variant='body' className='text-sm'>
                View App info
              </Text>
            </TooltipContent>
          </Tooltip>
        </div>
      ) : (
        <div className='m-0.5'>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex items-center cursor-pointer' onClick={() => setModalOpen(true)}>
                <Icon name='pencil' className='h-4 w-4 text-text-default hover:text-text-title' />
              </span>
            </TooltipTrigger>
            <TooltipContent className='flex items-center px-2 py-1 z-[60]'>
              <Text variant='body' className='text-sm'>
                Edit App
              </Text>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </>
  ) : null
})

UpdateAppModal.displayName = 'UpdateAppModal'

export default UpdateAppModal
